export const RECENTLY_VIEWED_PRODUCTS_KEY = "recentlyViewed";
export const RECENTLY_VIEWED_PRODUCTS_CHANGED_EVENT = "recentlyViewedProductsChanged";

const MAX_RECENTLY_VIEWED_PRODUCTS = 10;
const INVALID_IMAGE_VALUES = new Set([
    "",
    "no image",
    "null",
    "undefined",
    "false",
    "n/a",
    "na",
]);

const getStorage = () => {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        return window.localStorage;
    } catch (error) {
        return null;
    }
};

export const getFirstValidImageUrl = (...values) => {
    for (const value of values) {
        if (Array.isArray(value)) {
            const imageUrl = getFirstValidImageUrl(...value);
            if (imageUrl) {
                return imageUrl;
            }
            continue;
        }

        if (value && typeof value === "object") {
            const imageUrl = getFirstValidImageUrl(
                value.url,
                value.secure_url,
                value.location,
                value.path
            );
            if (imageUrl) {
                return imageUrl;
            }
            continue;
        }

        if (typeof value !== "string") {
            continue;
        }

        const imageUrl = value.trim();
        if (!INVALID_IMAGE_VALUES.has(imageUrl.toLowerCase())) {
            return imageUrl;
        }
    }

    return "";
};

export const getProductImageUrl = (product) => getFirstValidImageUrl(
    product?.featurerdImage,
    product?.featuredImage,
    product?.image,
    product?.thumbnail,
    product?.media?.photo,
    product?.media?.photos,
    product?.photos,
    product?.images
);

export const normalizeRecentlyViewedProduct = (product) => {
    if (!product || typeof product !== "object") {
        return null;
    }

    const id = product.id || product._id || product.productId;

    if (!id) {
        return null;
    }

    return {
        id,
        name: product.name || product.productName || "Unknown",
        image: getProductImageUrl(product),
        totalPrice: product.totalPrice || 0,
        sellingPrice: product.sellingPrice || 0,
    };
};

export const createRecentlyViewedProduct = (productData) => {
    const product = productData?.product;

    if (!product) {
        return null;
    }

    return normalizeRecentlyViewedProduct({
        ...product,
        id: product._id,
        name: product.productName,
        totalPrice: productData?.totalPrice,
        sellingPrice: productData?.sellingPrice,
    });
};

export const readRecentlyViewedProducts = () => {
    const storage = getStorage();

    if (!storage) {
        return [];
    }

    try {
        const storedProducts = JSON.parse(storage.getItem(RECENTLY_VIEWED_PRODUCTS_KEY)) || [];

        if (!Array.isArray(storedProducts)) {
            return [];
        }

        return storedProducts
            .map(normalizeRecentlyViewedProduct)
            .filter(Boolean)
            .slice(0, MAX_RECENTLY_VIEWED_PRODUCTS);
    } catch (error) {
        return [];
    }
};

export const writeRecentlyViewedProducts = (products) => {
    const storage = getStorage();

    if (!storage) {
        return false;
    }

    try {
        const normalizedProducts = Array.isArray(products)
            ? products.map(normalizeRecentlyViewedProduct).filter(Boolean).slice(0, MAX_RECENTLY_VIEWED_PRODUCTS)
            : [];

        storage.setItem(RECENTLY_VIEWED_PRODUCTS_KEY, JSON.stringify(normalizedProducts));
        return true;
    } catch (error) {
        return false;
    }
};

export const notifyRecentlyViewedProductsChanged = (products) => {
    if (typeof window === "undefined") {
        return;
    }

    window.dispatchEvent(new CustomEvent(RECENTLY_VIEWED_PRODUCTS_CHANGED_EVENT, {
        detail: { products },
    }));
};
